import { CheckCircle2 } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";

interface EmptyStateProps {
  title: string;
  description?: string;
  /** Override the default illustration with a custom node (e.g. a single Lucide glyph). */
  illustration?: ReactNode;
  /** Optional call-to-action, e.g. a `<Button>`. */
  action?: ReactNode;
  className?: string;
}

/**
 * Centered empty state with a small 2-color line illustration, a warm title,
 * and optional copy + action. Use for empty lists, tables, and
 * filtered "no results" views. Mirrors the `.empty` UI-kit spec.
 */
export function EmptyState({ title, description, illustration, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center",
        className,
      )}
    >
      <div className="mb-1">{illustration ?? <DefaultIllustration />}</div>
      <h3 className="text-16-bold text-fg-1">{title}</h3>
      {description && <p className="max-w-[420px] text-[13px] leading-[1.7] text-fg-2">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Small flat 2-color line drawing: a dashed document with a "done" check badge. */
function DefaultIllustration() {
  return (
    <div className="relative" aria-hidden="true">
      <div className="flex h-[60px] w-[52px] flex-col justify-center gap-1.5 rounded-[var(--radius-sm)] border-2 border-dashed border-primary-300 px-2.5">
        <span className="h-[3px] w-7 rounded-full bg-primary-300" />
        <span className="h-[3px] w-8 rounded-full bg-primary-300" />
        <span className="h-[3px] w-5 rounded-full bg-primary-300" />
      </div>
      <span className="absolute -bottom-1.5 -right-1.5 rounded-full bg-white p-0.5">
        <CheckCircle2 className="h-5 w-5 text-success" strokeWidth={2} />
      </span>
    </div>
  );
}
